/**
 * Utility functions for timeline calculations and event organization
 */
import { resolveDisplayDate } from '@/lib/date-utils';

// Constants for fixed world history view
export const MIN_YEAR = -4100;
export const MAX_YEAR = 400; // Based on user request to focus on biblical context

/**
 * Swim lane configuration
 */
export const SWIM_LANES = [
    {
        id: 'biblical-composition',
        label: 'Biblical Composition',
        categories: ['biblical-composition'] as HistoricalEvent['categories'][number]['category'][],
        color: 'hsl(210, 60%, 50%)', // Blue
        icon: '📜'
    },
    {
        id: 'biblical-narrative',
        label: 'Biblical Narrative',
        categories: ['biblical-narrative'] as HistoricalEvent['categories'][number]['category'][],
        color: 'hsl(140, 50%, 45%)', // Green
        icon: '📖'
    },
    {
        id: 'israel-ruler',
        label: 'Israel Rulers',
        categories: ['israel-ruler'] as HistoricalEvent['categories'][number]['category'][],
        color: 'hsl(30, 80%, 55%)', // Orange
        icon: '👑'
    },
    {
        id: 'israel-prophet',
        label: 'Israel Prophets',
        categories: ['israel-prophet'] as HistoricalEvent['categories'][number]['category'][],
        color: 'hsl(280, 60%, 55%)', // Purple
        icon: '📣'
    },
    {
        id: 'empires-peoples',
        label: 'Empires & Peoples',
        categories: ['empire', 'people'] as HistoricalEvent['categories'][number]['category'][],
        color: 'hsl(0, 0%, 50%)', // Gray
        icon: '🌍'
    },
    {
        id: 'culture-society',
        label: 'Culture & Society',
        categories: ['religious', 'cultural', 'economic', 'technological', 'natural', 'diaspora'] as HistoricalEvent['categories'][number]['category'][],
        color: 'hsl(180, 50%, 45%)', // Teal/Cyan
        icon: '🏺'
    },
    {
        id: 'political',
        label: 'Political',
        categories: ['political'] as HistoricalEvent['categories'][number]['category'][],
        color: 'hsl(260, 60%, 55%)', // Purple-blue
        icon: '🏛️'
    },
    {
        id: 'military',
        label: 'Military',
        categories: ['military'] as HistoricalEvent['categories'][number]['category'][],
        color: 'hsl(0, 70%, 50%)', // Red
        icon: '⚔️'
    }
];

export type SwimLaneId = typeof SWIM_LANES[number]['id'];

/**
 * Generate a consistent color shade for a subcategory
 * Uses a hash of the subcategory name to generate a consistent hue variation
 */
function getSubcategoryColorShade(subcategory: string, baseHue: number, category?: string): string {
    // Simple hash function to get a consistent number from string
    let hash = 0;
    for (let i = 0; i < subcategory.length; i++) {
        hash = ((hash << 5) - hash) + subcategory.charCodeAt(i);
        hash = hash & hash; // Convert to 32-bit integer
    }

    // For empire/people categories (gray base), use grayscale variations
    if (category === 'empire' || category === 'people') {
        const lightness = 35 + (Math.abs(hash) % 25); // 35-60% for variety
        return `hsl(0, 0%, ${lightness}%)`;
    }

    // Use hash to vary the hue slightly (±30 degrees) and lightness (40-55%)
    const hueVariation = (Math.abs(hash) % 60) - 30; // -30 to +30
    const lightness = 40 + (Math.abs(hash >> 8) % 16); // 40-55%

    return `hsl(${baseHue + hueVariation}, 60%, ${lightness}%)`;
}

/**
 * Get base hue for a category
 */
function getBaseHueForCategory(category: string): number {
    const hueMap: Record<string, number> = {
        'biblical-composition': 210,  // Blue
        'biblical-narrative': 140,    // Green
        'israel-ruler': 30,           // Orange
        'israel-prophet': 280,        // Purple
        'empire': 0,                  // Gray (hue doesn't matter for gray, saturation is 0)
        'people': 0,                  // Gray (hue doesn't matter for gray, saturation is 0)
        'political': 0,               // Red
        'military': 0,                // Red
        'religious': 180,             // Teal/Cyan
        'cultural': 180,              // Teal/Cyan
        'economic': 180,              // Teal/Cyan
        'technological': 180,         // Teal/Cyan
        'natural': 180,               // Teal/Cyan
        'diaspora': 180               // Teal/Cyan
    };

    return hueMap[category] || 0;
}

/**
 * Get color for a swim lane (including dynamic sub-lanes)
 */
export function getSwimLaneColor(laneId: string): string {
    // Check if it's a dynamic sub-lane (contains a hyphen after the category)
    const match = laneId.match(/^([a-z-]+?)-(.+)$/);
    if (match) {
        const [, category, subcategory] = match;
        // Check if this is actually a subcategory (not just a compound category name like 'israel-ruler')
        if (DYNAMIC_SUBLANE_CATEGORIES.includes(category) ||
            DYNAMIC_SUBLANE_CATEGORIES.includes(laneId.split('-').slice(0, 2).join('-'))) {
            const baseHue = getBaseHueForCategory(category);
            return getSubcategoryColorShade(subcategory, baseHue, category);
        }
    }

    // Otherwise, find in standard lanes
    const lane = SWIM_LANES.find(l => l.id === laneId);
    return lane?.color || 'hsl(0, 0%, 50%)'; // Default gray if not found
}

/**
 * Event with swim lane assignment
 */
export interface TimelineEvent {
    title: string;
    summary: string;
    description: string;
    relevance: string;
    displayDateRange: string;
    sortableYearStart: number;
    sortableYearEnd: number;
    date?: DateProposal; // Full date object with chronology and rationale
    categories: Array<{ category: string; subcategory?: string }>;
    confidence?: Confidence;
    sources?: Source[];
    verseRef?: string;
    keyFigures?: string[];
    laneId: string; // Can be SwimLaneId or dynamic sub-lane like "empire-roman"
    importance?: Importance;
    source: 'passage' | 'book' | 'reference'; // Where the event data came from
    eventType?: HistoricalEvent['eventType']; // Type of event (only for reference events)

    // Additional HistoricalEvent fields
    alternativeDates?: DateProposal[];
    impact?: string;
    archaeologicalEvidence?: string;
    historicity?: HistoricalEvent['historicity'];
    region?: string;
    empire?: string;
    biblicalReferences?: Array<{
        book: string;
        passages?: string[];
        relevance: string;
    }>;

    // AI-generated image
    imageUrl?: string;
    generatedBy?: string;
}

/**
 * Categories that should have dynamic sub-lanes based on subcategory
 */
const DYNAMIC_SUBLANE_CATEGORIES = [
    'empire',
    'people'
];

/**
 * Assign an event to a swim lane based on its primary category and subcategory
 * For categories with subcategories, creates dynamic sub-lanes
 */
export function assignEventToLane(
    event: HistoricalEventByPassage | HistoricalEventByBook | HistoricalEvent
): string {
    const primaryCategory = event.categories?.[0]?.category;
    const subcategory = event.categories?.[0]?.subcategory;

    // For categories with subcategories, create subcategory lanes
    if (subcategory && DYNAMIC_SUBLANE_CATEGORIES.includes(primaryCategory)) {
        return `${primaryCategory}-${subcategory.toLowerCase().replace(/\s+/g, '-')}`;
    }

    // For other categories, use the standard swim lane
    for (const lane of SWIM_LANES) {
        if (lane.categories.includes(primaryCategory)) {
            return lane.id;
        }
    }

    // Default to culture-society for unknown categories
    return 'culture-society';
}

/**
 * Convert various event types to TimelineEvent
 */
export function convertToTimelineEvent(
    event: HistoricalEventByPassage | HistoricalEventByBook | HistoricalEvent,
    source: 'passage' | 'book' | 'reference',
    preferredChronology?: BiblicalChronology
): TimelineEvent {
    const laneId = assignEventToLane(event);

    // Resolve date based on preferred chronology
    const { activeDate, alternatives } = resolveDisplayDate(
        event.date,
        event.alternativeDates,
        preferredChronology
    );

    // Handle HistoricalEvent type (has eventType field which the others don't have)
    if ('eventType' in event) {
        return {
            title: event.title,
            summary: event.summary || '',
            description: event.description || '',
            relevance: event.relevance || '',
            displayDateRange: activeDate.displayDate,
            sortableYearStart: activeDate.startYear,
            sortableYearEnd: activeDate.endYear || activeDate.startYear,
            date: activeDate,
            categories: event.categories,
            importance: event.importance,
            sources: event.sources || activeDate.sources,
            keyFigures: event.keyFigures,
            laneId,
            source,
            eventType: event.eventType,
            // Additional HistoricalEvent fields
            alternativeDates: alternatives,
            impact: event.impact,
            archaeologicalEvidence: event.archaeologicalEvidence,
            historicity: event.historicity,
            region: event.region,
            empire: event.empire,
            biblicalReferences: event.biblicalReferences,
            imageUrl: event.image?.imageUrl,
            generatedBy: event.generatedBy
        };
    }

    // Handle HistoricalEventByPassage and HistoricalEventByBook
    return {
        title: event.title,
        summary: event.summary,
        description: event.description,
        relevance: event.relevance,
        displayDateRange: activeDate.displayDate,
        sortableYearStart: activeDate.startYear,
        sortableYearEnd: activeDate.endYear || activeDate.startYear,
        date: activeDate,
        categories: event.categories,
        confidence: 'confidence' in event ? event.confidence : undefined,
        sources: event.sources || activeDate.sources,
        verseRef: 'verseRef' in event ? (event as any).verseRef : undefined,
        keyFigures: 'keyFigures' in event ? (event as any).keyFigures : undefined,
        alternativeDates: alternatives,
        laneId,
        source,
        generatedBy: (event as any).generatedBy || (event as any).source
    };
}

/**
 * Calculate date range for timeline with padding
 */
export function calculateDateRange(
    events: TimelineEvent[],
    passageDate?: { startYear: number; endYear?: number },
    padding: number = 100,
    maxRangeYears: number = 2000 // Increased cap to allow wider zoom out, but still prevent extreme squashing
): { minYear: number; maxYear: number } {
    let minYear = Infinity;
    let maxYear = -Infinity;

    // When zoomed in tight (small padding), focus on passage date only
    // When zoomed out (large padding), include events
    const isZoomedIn = padding < 50;

    // Include passage date if available
    if (passageDate) {
        minYear = Math.min(minYear, passageDate.startYear);
        maxYear = Math.max(maxYear, passageDate.endYear || passageDate.startYear);
    }

    // Only include events in range calculation when zoomed out
    if (!isZoomedIn) {
        // Include all events, but cap their contribution to the range
        events.forEach(event => {
            // For events with large ranges, only include their start/end if they're close to the passage date
            const eventDuration = event.sortableYearEnd - event.sortableYearStart;

            if (passageDate && eventDuration > maxRangeYears / 2) {
                // For large events, only expand the range if the passage date is within the event
                const passageYear = passageDate.startYear;
                if (event.sortableYearStart <= passageYear && event.sortableYearEnd >= passageYear) {
                    // Passage is within the event - cap the range to maxRangeYears around the passage
                    const cappedMin = Math.max(event.sortableYearStart, passageYear - maxRangeYears / 2);
                    const cappedMax = Math.min(event.sortableYearEnd, passageYear + maxRangeYears / 2);
                    minYear = Math.min(minYear, cappedMin);
                    maxYear = Math.max(maxYear, cappedMax);
                }
            } else {
                // Normal events - include their full range
                minYear = Math.min(minYear, event.sortableYearStart);
                maxYear = Math.max(maxYear, event.sortableYearEnd);
            }
        });
    }

    // Set defaults if no data
    if (!isFinite(minYear)) minYear = -586;
    if (!isFinite(maxYear)) maxYear = 70;

    // Add padding (allow very small padding for tight zoom)
    minYear -= padding;
    maxYear += padding;

    // When zoomed in, ensure we don't go below minimum range
    // When zoomed out, allow larger ranges
    const minTotalRange = isZoomedIn ? 10 : 50;
    const totalRange = maxYear - minYear;
    if (totalRange < minTotalRange) {
        const center = (minYear + maxYear) / 2;
        minYear = center - minTotalRange / 2;
        maxYear = center + minTotalRange / 2;
    }

    return { minYear, maxYear };
}

/**
 * Format year for display (BC/AD)
 */
export function formatYear(year: number): string {
    if (year < 0) {
        // Astronomical year system: -700 = 700 BC
        return `${Math.abs(year)} BC`;
    } else if (year === 0) {
        // Astronomical year 0 is 1 BC
        return '1 BC';
    } else {
        return `AD ${year}`;
    }
}

/**
 * Calculate pixel position for a year on the timeline
 */
export function yearToPixel(year: number, minYear: number, maxYear: number, width: number): number {
    const range = maxYear - minYear;
    const offset = year - minYear;
    return (offset / range) * width;
}

/**
 * Calculate year from pixel position on the timeline
 */
export function pixelToYear(pixel: number, minYear: number, maxYear: number, width: number): number {
    const range = maxYear - minYear;
    const offset = (pixel / width) * range;
    return Math.round(minYear + offset);
}

/**
 * Generate year tick marks for the timeline axis
 */
export function generateYearTicks(
    minYear: number,
    maxYear: number,
    width: number,
    visibleStartYear?: number,
    visibleEndYear?: number
): Array<{ year: number; isMajor: boolean; isMicro?: boolean; showLabel?: boolean; position: number }> {
    const range = maxYear - minYear;
    const ticks: Array<{ year: number; isMajor: boolean; isMicro?: boolean; showLabel?: boolean; position: number }> = [];

    // Determine tick interval based on range
    // Adjusted to reduce tick density when zoomed out
    let majorInterval: number;
    let minorInterval: number;
    let microInterval: number;

    // Determine intervals based on pixels per year (zoomed in) vs total range
    // We can approximate pixels per year from width / range
    const pixelsPerYear = width / range;

    if (pixelsPerYear >= 50) {         // Very zoomed in (>50px/year)
        majorInterval = 5;
        minorInterval = 1;
        microInterval = 0.2;
    } else if (pixelsPerYear >= 20) {  // Zoomed in (>20px/year)
        majorInterval = 10;
        minorInterval = 1;
        microInterval = 0;
    } else if (pixelsPerYear >= 10) {  // Medium (>10px/year)
        majorInterval = 20;
        minorInterval = 2;
        microInterval = 0;
    } else if (pixelsPerYear >= 5) {   // Medium (>5px/year)
        majorInterval = 50;
        minorInterval = 5;
        microInterval = 0;
    } else if (pixelsPerYear >= 2) {   // Medium out (>2px/year)
        majorInterval = 100;
        minorInterval = 10;
        microInterval = 0;
    } else if (pixelsPerYear >= 1) {   // Zoomed out (>1px/year)
        majorInterval = 200;
        minorInterval = 20;
        microInterval = 0;
    } else if (pixelsPerYear >= 0.5) { // Zoomed out (>0.5px/year)
        majorInterval = 500;
        minorInterval = 50;
        microInterval = 0;
    } else {                           // Very zoomed out
        majorInterval = 1000;
        minorInterval = 100;
        microInterval = 0;
    }

    // Define the range to generate ticks for
    // If visible range is provided, only generate ticks for that range (plus padding)
    let tickStartYear = minYear;
    let tickEndYear = maxYear;

    if (visibleStartYear !== undefined && visibleEndYear !== undefined) {
        // Add padding to ensure smooth scrolling (ticks appear before entering viewport)
        const paddingYears = (visibleEndYear - visibleStartYear) * 0.5;
        tickStartYear = Math.max(minYear, Math.floor(visibleStartYear - paddingYears));
        tickEndYear = Math.min(maxYear, Math.ceil(visibleEndYear + paddingYears));
    }

    // Generate micro ticks (smallest, no labels)
    if (microInterval > 0 && microInterval >= 1) {
        const microStartYear = Math.floor(tickStartYear / microInterval) * microInterval;
        const microEndYear = Math.ceil(tickEndYear / microInterval) * microInterval;

        for (let year = microStartYear; year <= microEndYear; year += microInterval) {
            // Skip if this year will be a minor or major tick
            if (year % minorInterval !== 0) {
                const position = yearToPixel(year, minYear, maxYear, width);
                ticks.push({ year, isMajor: false, isMicro: true, showLabel: false, position });
            }
        }
    }

    // Generate minor and major ticks (with labels)
    const startLoopYear = Math.floor(tickStartYear / minorInterval) * minorInterval;
    const endLoopYear = Math.ceil(tickEndYear / minorInterval) * minorInterval;

    let lastLabeledYear: number | null = null;

    for (let year = startLoopYear; year <= endLoopYear; year += minorInterval) {
        const isMajor = year % majorInterval === 0;
        const position = yearToPixel(year, minYear, maxYear, width);

        // Determine if this tick should show a label
        let showLabel = false;
        if (isMajor) {
            // Always show major tick labels
            showLabel = true;
            lastLabeledYear = year;
        } else {
            // For minor ticks, only show label if:
            // 1. No label was shown yet, OR
            // 2. The last labeled tick was at least 2 intervals ago (skip one minor tick between labels)
            if (lastLabeledYear === null || Math.abs(year - lastLabeledYear) >= minorInterval * 2) {
                showLabel = true;
                lastLabeledYear = year;
            }
        }

        ticks.push({ year, isMajor, isMicro: false, showLabel, position });
    }

    return ticks;
}

/**
 * Swim lane metadata (can be a parent lane or subcategory lane)
 */
export interface SwimLaneMetadata {
    id: string;
    label: string;
    icon: string;
    color: string;
    isSubcategory: boolean;
    parentLaneId?: string;
    parentLabel?: string;
}

/**
 * Get icon for a category
 */
function getIconForCategory(category: string): string {
    const iconMap: Record<string, string> = {
        'biblical-composition': '📜',
        'biblical-narrative': '📖',
        'israel-ruler': '👑',
        'israel-prophet': '📣',
        'empire': '🏛️',
        'people': '🏘️',
        'political': '⚖️',
        'military': '⚔️',
        'religious': '🙏',
        'cultural': '🎭',
        'economic': '💰',
        'technological': '⚙️',
        'natural': '🌊',
        'diaspora': '🚶'
    };
    return iconMap[category] || '📌';
}

/**
 * Build swim lanes from events (including subcategory lanes)
 */
export function buildSwimLanesFromEvents(events: TimelineEvent[]): SwimLaneMetadata[] {
    const lanes: SwimLaneMetadata[] = [];

    // Collect all unique lane IDs from events
    const allLaneIds = new Set<string>();
    events.forEach(event => {
        allLaneIds.add(event.laneId);
    });

    // Group lane IDs by parent swim lane
    const lanesByParent = new Map<string, Set<string>>();

    SWIM_LANES.forEach(lane => {
        const subLanes = new Set<string>();

        allLaneIds.forEach(laneId => {
            // Check if this lane ID belongs to this parent lane
            const belongsToLane = lane.categories.some(cat => {
                // Direct match (no subcategory)
                if (laneId === lane.id) return true;

                // Check if it's a sub-lane of this category
                return laneId.startsWith(`${cat}-`);
            });

            if (belongsToLane) {
                subLanes.add(laneId);
            }
        });

        if (subLanes.size > 0) {
            lanesByParent.set(lane.id, subLanes);
        }
    });

    // Build the lane list - always include all parent lanes
    SWIM_LANES.forEach(lane => {
        const subLanes = lanesByParent.get(lane.id);

        // Check if we have any sub-lanes (lanes with subcategories)
        const hasSubLanes = subLanes && Array.from(subLanes).some(id => id !== lane.id);

        if (hasSubLanes) {
            // Add each sub-lane
            Array.from(subLanes!).sort().forEach(subLaneId => {
                if (subLaneId === lane.id) return; // Skip the parent lane itself

                // Extract category and subcategory
                const parts = subLaneId.split('-');
                let category = parts[0];
                let subcategory = parts.slice(1).join('-');

                // Handle compound categories like 'israel-ruler'
                if (parts.length > 2 && DYNAMIC_SUBLANE_CATEGORIES.includes(`${parts[0]}-${parts[1]}`)) {
                    category = `${parts[0]}-${parts[1]}`;
                    subcategory = parts.slice(2).join('-');
                }

                const label = subcategory.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

                lanes.push({
                    id: subLaneId,
                    label: label,
                    icon: getIconForCategory(category),
                    color: getSwimLaneColor(subLaneId),
                    isSubcategory: true,
                    parentLaneId: lane.id,
                    parentLabel: lane.label
                });
            });
        } else {
            // No sub-lanes, add the parent lane directly (even if no events)
            lanes.push({
                id: lane.id,
                label: lane.label,
                icon: lane.icon,
                color: lane.color,
                isSubcategory: false
            });
        }
    });

    return lanes;
}

/**
 * Group events by lane and handle overlaps
 */
export function organizeEventsByLane(
    events: TimelineEvent[]
): Map<string, TimelineEvent[][]> {
    const laneMap = new Map<string, TimelineEvent[][]>();

    // Group events by lane
    const eventsByLane = new Map<string, TimelineEvent[]>();
    events.forEach(event => {
        const laneEvents = eventsByLane.get(event.laneId) || [];
        laneEvents.push(event);
        eventsByLane.set(event.laneId, laneEvents);
    });

    // Sort events within each lane by start year
    eventsByLane.forEach((laneEvents, laneId) => {
        laneEvents.sort((a, b) => a.sortableYearStart - b.sortableYearStart);

        // Handle overlaps by creating rows
        const rows: TimelineEvent[][] = [];
        laneEvents.forEach(event => {
            // Find a row where this event doesn't overlap
            let placed = false;
            for (const row of rows) {
                const lastEvent = row[row.length - 1];
                // Check if there's enough space (no overlap)
                if (lastEvent.sortableYearEnd < event.sortableYearStart) {
                    row.push(event);
                    placed = true;
                    break;
                }
            }
            // If no suitable row found, create a new one
            if (!placed) {
                rows.push([event]);
            }
        });

        laneMap.set(laneId, rows);
    });

    return laneMap;
}

/**
 * Era band for geographic overlay
 */
/**
 * Parse a passage reference string to extract chapter range
 * Handles formats like "5:1-8" (single chapter) or "5:1-7:8" (multi-chapter)
 * Also handles simple formats like "5" (whole chapter) or "5:4" (single verse)
 * Also handles full references like "Luke 9:10-17" by extracting just the chapter/verse part
 */
function parsePassageReference(ref: string): { startChapter: number; endChapter: number } | null {
    if (!ref) return null;

    // Strip book name if present (e.g., "Luke 9:10-17" -> "9:10-17")
    // Look for the last space followed by a number
    const lastSpaceIndex = ref.lastIndexOf(' ');
    if (lastSpaceIndex !== -1) {
        const afterSpace = ref.substring(lastSpaceIndex + 1);
        // Check if what follows is a chapter/verse reference
        if (/^\d/.test(afterSpace)) {
            ref = afterSpace;
        }
    }

    // Handle simple chapter reference like "5"
    if (!ref.includes(':') && !ref.includes('-')) {
        const chapter = parseInt(ref, 10);
        return isNaN(chapter) ? null : { startChapter: chapter, endChapter: chapter };
    }

    // Handle single verse like "5:4"
    if (ref.includes(':') && !ref.includes('-')) {
        const chapter = parseInt(ref.split(':')[0], 10);
        return isNaN(chapter) ? null : { startChapter: chapter, endChapter: chapter };
    }

    // Handle ranges like "5:1-8" or "5:1-7:8"
    const parts = ref.split('-');
    if (parts.length !== 2) return null;

    const startParts = parts[0].split(':');
    const endParts = parts[1].split(':');

    const startChapter = parseInt(startParts[0], 10);

    // If end part has a colon, it's a multi-chapter range like "5:1-7:8"
    // Otherwise it's same chapter like "5:1-8"
    const endChapter = endParts.length > 1
        ? parseInt(endParts[0], 10)
        : startChapter;

    if (isNaN(startChapter) || isNaN(endChapter)) return null;

    return { startChapter, endChapter };
}

/**
 * Check if two chapter ranges overlap
 */
function chapterRangesOverlap(
    range1: { startChapter: number; endChapter: number },
    range2: { startChapter: number; endChapter: number }
): boolean {
    // Ranges overlap if one starts before the other ends
    return range1.startChapter <= range2.endChapter && range2.startChapter <= range1.endChapter;
}

/**
 * Check if a reference event's biblical references match the current passage
 */
export function eventMatchesPassage(
    event: HistoricalEvent,
    passage: Passage
): boolean {
    if (!event.biblicalReferences || event.biblicalReferences.length === 0) {
        return false;
    }

    // Parse the passage's chapter range
    const passageRange = parsePassageReference(passage.ref);
    if (!passageRange) {
        return false;
    }

    // Check if any biblical reference matches the passage's book and chapter range
    return event.biblicalReferences.some(ref => {
        // Must match the book
        if (ref.book !== passage.book) {
            return false;
        }

        // If no specific passages listed, consider it a match for the whole book
        if (!ref.passages || ref.passages.length === 0) {
            return true;
        }

        // Check if any of the referenced passages overlap with the current passage by chapter
        return ref.passages.some(refPassage => {
            const refRange = parsePassageReference(refPassage);
            if (!refRange) {
                return false;
            }

            return chapterRangesOverlap(passageRange, refRange);
        });
    });
}

